import {
  TCapacityBoardLoadType,
  TCapacityBoardUtilizationData,
  TFactoryIdNameDTO,
  TRESPONSE_GET_CAPACITY_BOARD,
} from '@monorepo/types';
import { TIMELINE_MODEL } from '../timeline.model';
import { TEntityActions, TRange, TTimelineModel } from '../timeline.types';

export const CAPACITY_BOARD_LOAD_TYPE_DISPLAY_NAME: Record<TCapacityBoardLoadType, string> = {
  HIGHLY_OVERLOADED: 'Highly Overloaded',
  OVERLOADED: 'Overloaded',
  UNIFORMLY_LOADED: 'Uniformly Loaded',
  UNDERLOADED: 'Underloaded',
  NO_CAPACITY: 'No Capacity',
};

export const CAPACITY_BOARD_LOAD_TYPE_COLOR: Record<TCapacityBoardLoadType, string> = {
  HIGHLY_OVERLOADED: '#FF6140',
  OVERLOADED: '#EA9B4A',
  UNIFORMLY_LOADED: '#40BF76',
  UNDERLOADED: '#A3A3A7',
  NO_CAPACITY: '#CBCBCD',
};

type TCapacityBoardTimelineBoardLineData = {
  data: TCapacityBoardUtilizationData[];
  factory?: TFactoryIdNameDTO;
};

const CAPACITY_BOARD_STRIP_START_PADDING = '02:00:00';
const CAPACITY_BOARD_STRIP_END_PADDING = '22:00:00';

function getCapacityBoardLinesData(
  dto: TRESPONSE_GET_CAPACITY_BOARD['capacityBoardDTO'],
): TCapacityBoardTimelineBoardLineData[] {
  if (!dto.factoryLevelUtilizationData) return [{ data: dto.accountLevelUtilizationData }];

  return [{ data: dto.accountLevelUtilizationData }].concat(
    dto.factoryLevelUtilizationData.map(
      (data): TCapacityBoardTimelineBoardLineData => ({
        data: data.utilizationStrips,
        factory: data.factory,
      }),
    ),
  );
}

export const CAPACITY_BOARD_TIMELINE_MODEL = {
  createModel: (capacityBoardRes: TRESPONSE_GET_CAPACITY_BOARD): TCapacityBoardTimelineModel => {
    return TIMELINE_MODEL.createTimelineModel(
      capacityBoardRes.boardResponseView,
      [capacityBoardRes.capacityBoardDTO],
      (dto) => ({
        id: 'capacity_board_group',
        data: dto,
        entities: getCapacityBoardLinesData(dto).map((lineData) => ({
          id: lineData.factory?.factoryName ?? 'capacity_board_all_factory_line',
          name: { displayTxt: '', isClickable: false },
          metadata: {
            subTitle: (lineData.factory?.factoryName ?? 'All Factory') + ' Capacity',
            leftCount: 0,
            rightCount: 0,
            title: '',
          },
          data: lineData,
          elements: lineData.data.map((ud, idx): TCapacityBoardStripElement => {
            // [NOTE]: Timestamps to add some padding between strips
            const startDate = `${ud.dateRange.from} ${CAPACITY_BOARD_STRIP_START_PADDING}`;
            const endDate = `${ud.dateRange.to} ${CAPACITY_BOARD_STRIP_END_PADDING}`;

            return {
              id: `capacity_board_strip_${lineData.factory?.factoryId ?? 'account'}_${idx}`,
              data: ud,
              color: CAPACITY_BOARD_LOAD_TYPE_COLOR[ud.status],
              iconList: [],
              mask: false,
              name: `Capacity: ${ud.utilization}%, [${CAPACITY_BOARD_LOAD_TYPE_DISPLAY_NAME[ud.status]}]`,
              range: { startDate, endDate } as TRange,
              stackCount: 0,
              type: 'STRIP' as const,
              textList: [
                { value: ud.utilization + '%', color: '#000', align: 'center', justify: 'center', fontSizeInPx: 24 },
              ],
            };
          }),
        })),
        holidays: [],
        name: 'Capacity Board',
      }),
      //[NOTE]: only show left panel when we have data for multiple factories
      !capacityBoardRes.capacityBoardDTO.factoryLevelUtilizationData,
    );
  },

  changeView: (
    type: TTimelineModel['boardView']['boardViewType'],
    timelineModel: TCapacityBoardTimelineModel,
  ): TCapacityBoardTimelineModel => {
    return TIMELINE_MODEL.changeTimelineType(type, timelineModel);
  },
};

type TCapacityBoardStripElement =
  TCapacityBoardTimelineModel['entities'][number]['entities'][number]['elements'][number];

export type TCapacityBoardTimelineModel = TTimelineModel<
  TRESPONSE_GET_CAPACITY_BOARD['capacityBoardDTO'],
  TCapacityBoardTimelineBoardLineData,
  TCapacityBoardUtilizationData
>;
export type TCapacityBoardEntityActions = TEntityActions<
  TRESPONSE_GET_CAPACITY_BOARD['capacityBoardDTO'],
  TCapacityBoardTimelineBoardLineData,
  TCapacityBoardUtilizationData
>;
